import { isEmpty } from '@/common/helpers';
import { AbilityBuilder, Ability } from '@casl/ability';
import icons from '@/shared/icons';
import { intersection, sortBy } from 'lodash';
import exportUtils from './exportUtils';

export default {
    parseSortField(requestQuery, fieldsConfig) {
        // will go over the requestQuery and replace the the sort field with the
        // server sort field name
        if (!isEmpty(requestQuery)) {
            let newQuery = { ...requestQuery };
            const sortBy = newQuery.sort;
            if (sortBy && fieldsConfig[sortBy]) {
                const serverSortBy = fieldsConfig[sortBy].sortName;
                if (serverSortBy) {
                    newQuery.sort = serverSortBy;
                    return newQuery;
                }
            }
        }
        return requestQuery;
    },
    parseSortDirection(requestQuery) {
        const { desc, ...rest } = requestQuery;
        if (desc === true) {
            let newQuery = rest;
            if (newQuery.sort) {
                newQuery.sort = `${newQuery.sort}:desc`;
                return newQuery;
            }
        }
        return rest;
    },
    buildTableFields(viewName, tableName, fieldsConfig, appAbility) {
        // will genereate the data table component fields/headers
        let fields = [];
        if (!isEmpty(fieldsConfig)) {
            for (const fieldKey in fieldsConfig) {
                if (
                    appAbility.can(
                        'show',
                        `Fields/${viewName}/${tableName}/${fieldKey}`
                    )
                ) {
                    const fieldObject = fieldsConfig[fieldKey];
                    fieldObject.key = fieldKey;
                    fields.push(fieldObject);
                }
            }
            // need to sort the fields array
            fields = sortBy(fields, 'displayOrder');
            // need to map the fields to UI table header format
            fields = fields.map(item => {
                return {
                    text: item.label,
                    value: item.key,
                    sortable: item.sortable
                };
            });
        }
        return fields;
    },
    buildComputedFields(fieldsConfig) {
        let fields = [];
        if (!isEmpty(fieldsConfig)) {
            for (const fieldKey in fieldsConfig) {
                const fieldObject = fieldsConfig[fieldKey];
                if (fieldObject.computed) {
                    fields.push({
                        value: fieldKey,
                        computed: fieldObject.computed
                    });
                }
            }
        }
        return fields;
    },
    buildExportFields(fieldsConfig) {
        // will genereate the export fields definition for the selected table.
        let exportFields = {};
        if (!isEmpty(fieldsConfig)) {
            let fields = [];
            for (const fieldKey in fieldsConfig) {
                const fieldObject = fieldsConfig[fieldKey];
                if (fieldObject.exportable === true) {
                    fieldObject.key = fieldKey;
                    fields.push(fieldObject);
                }
            }
            // need to sort the fields array
            fields = sortBy(fields, 'displayOrder');
            // TODO: need to find a way to preserve the fields order for the export...
            fields.forEach(item => {
                if (item.exportFunction) {
                    exportFields[item.label] = {
                        field: item.key,
                        callback: exportUtils[item.exportFunction]
                    };
                } else {
                    exportFields[item.label] = item.key;
                }
            });
        }
        return exportFields;
    },
    buildFilterFields(fieldsConfig) {
        // will genereate the filter fields definition for the selected table.
        let filterFields = [];
        if (!isEmpty(fieldsConfig)) {
            let fields = [];
            for (const fieldKey in fieldsConfig) {
                const fieldObject = fieldsConfig[fieldKey];
                if (fieldObject.filterable === true) {
                    fieldObject.key = fieldKey;
                    fields.push(fieldObject);
                }
            }
            // need to sort the fields array
            filterFields = sortBy(fields, 'displayOrder').map(item => {
                return {
                    name: item.key,
                    text: item.label
                };
            });
        }
        return filterFields;
    },
    buildDataTableAbilities(
        viewName,
        tableName,
        fieldsConfig,
        actionsConfig,
        userPermissions
    ) {
        // will add the data table abilities based on user permissions
        const { rules, can } = new AbilityBuilder(Ability);
        for (const fieldKey in fieldsConfig) {
            const fieldObject = fieldsConfig[fieldKey];
            fieldObject.key = fieldKey;
            if (
                this.canShowFeature(fieldObject, actionsConfig, userPermissions)
            ) {
                can('show', `Fields/${viewName}/${tableName}/${fieldKey}`);
            }
        }
        return rules;
    },
    buildViewButtonsAbilities(
        viewName,
        buttonsConfig,
        actionsConfig,
        userPermissions
    ) {
        // will generate buttons abilities based on the selected view and
        // user permissions
        const { rules, can } = new AbilityBuilder(Ability);
        // need to build the table fields
        for (const buttonKey in buttonsConfig) {
            const buttonObject = buttonsConfig[buttonKey];
            buttonObject.key = buttonKey;
            if (
                this.canShowFeature(
                    buttonObject,
                    actionsConfig,
                    userPermissions
                )
            ) {
                can('show', `Buttons/${viewName}/${buttonKey}`);
            }
        }
        return rules;
    },
    buildEntityAbilities(userPermissions) {
        // will build the database entity abilities based on permissions
        // for example can('edit', 'Book') or can('read', 'Book')
        // will build abilities for all entities in the user permissions array
        const { rules, can } = new AbilityBuilder(Ability);
        userPermissions.forEach(permission => {
            const parts = permission.split('_');
            if (parts.length > 1) {
                const action = parts.splice(-1, 1)[0].toLowerCase();
                const subject = parts
                    .map(item => this.toCapitalCase(item))
                    .join('');
                if (action && subject) {
                    can(action, subject);
                }
            } else {
                // the permission is not setup correctly and should be like
                // this DEVICE_READ...
            }
        });
        return rules;
    },
    buildUserInfoAbilities(userInfo) {
        const { rules, can } = new AbilityBuilder(Ability);
        if (!isEmpty(userInfo)) {
            // need to add extra rule for the showMsoUsers
            if (userInfo.xocAuth === true) {
                can('show', 'MsoUsers');
            }
        }
        return rules;
    },
    buildSidebarAbilities(userPermissions, conditionFunctions, featuresConfig) {
        // generate the user abilities based on some rules
        // conditionFunctions is an array of functions that can be used to evaluate the showCondition
        // the rules are driven from the featuresConfig
        const { rules, can } = new AbilityBuilder(Ability);
        for (const viewKey in featuresConfig.views) {
            let view = featuresConfig.views[viewKey];
            view.key = viewKey;
            let actionsObject = view['actions'];
            if (
                this.canShowFeature(
                    view,
                    actionsObject,
                    userPermissions,
                    conditionFunctions
                )
            ) {
                can('show', `Sidebar/${viewKey}`);
                can('route', view.route);
            }
        }
        return rules;
    },
    buildSidebarItems(appAbility, featuresConfig) {
        let sidebarItems = [];
        for (const viewKey in featuresConfig.views) {
            let view = featuresConfig.views[viewKey];
            if (
                view.showInMenu &&
                appAbility.can('show', `Sidebar/${viewKey}`)
            ) {
                sidebarItems.push({
                    title: view.title || viewKey,
                    icon: view.icon || icons.defaultItem,
                    route: view.route,
                    href: view.href,
                    sortOrder: view.sortOrder || 0,
                    appendToken: view.appendToken,
                    earlyAccess: view.earlyAccess
                });
            }
        }
        sidebarItems.sort((a, b) => {
            return a.sortOrder < b.sortOrder
                ? -1
                : a.sortOrder > b.sortOrder
                ? 1
                : 0;
        });
        return sidebarItems;
    },
    canShowFeature(
        featureObject,
        actionsObject,
        userPermissions,
        conditionFunctions
    ) {
        // will return true if the user has the right to see this feature
        // conditionFunctions is an array of functions that can be used when
        // evaluating the feature showCondition if it applies
        // this function will be used to evaluate nav menu items, table columns, and buttons.
        if (featureObject) {
            if (featureObject.show || featureObject.showInMenu) {
                if (
                    featureObject.hasOwnProperty('protected') &&
                    featureObject.protected
                ) {
                    if (
                        this.evalFeaturePermissions(
                            featureObject,
                            actionsObject,
                            userPermissions
                        )
                    ) {
                        return this.evalShowCondition(
                            featureObject,
                            conditionFunctions
                        );
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            } else {
                return false;
            }
        } else {
            return true;
        }
    },
    evalShowCondition(featureObject, conditionFunctions) {
        // returns true if the showCondition function will eveluate to true
        // the showCondition is a getter function which should evaluate to true or false
        // and can be called by getters[showCondition]
        if (!featureObject.hasOwnProperty('showCondition')) {
            return true;
        } else {
            if (
                featureObject.showCondition &&
                conditionFunctions[featureObject.showCondition]
            ) {
                return true;
            } else {
                return false;
            }
        }
    },
    evalFeaturePermissions(featureObject, actionsObject, userPermissions) {
        // will evaluate the fetures permissions against the user permissions
        // and return true if user has rights to show this feature
        let featurePermissions = [];
        if (
            featureObject &&
            featureObject.permissions &&
            featureObject.permissions.length
        ) {
            // evaluate element permissions
            featurePermissions = featureObject.permissions;
        } else if (featureObject && featureObject.action) {
            // evaluate action permissions
            if (
                actionsObject &&
                actionsObject[featureObject.action] &&
                actionsObject[featureObject.action].permissions
            ) {
                featurePermissions =
                    actionsObject[featureObject.action].permissions;
            }
        }
        if (!Array.isArray(userPermissions) || isEmpty(userPermissions)) {
            return false;
        } else if (
            Array.isArray(featurePermissions) &&
            featurePermissions.length
        ) {
            if (
                userPermissions.length &&
                intersection(featurePermissions, userPermissions).length
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    },
    toCapitalCase(inString) {
        // will capitalize the first char of a string
        if (inString && inString.length) {
            return (
                inString.charAt(0).toUpperCase() +
                inString.slice(1).toLowerCase()
            );
        } else {
            return inString;
        }
    },
    parseSortBy(sortBy, sortMap) {
        // will parse the db search sort field into a UI sort field
        if (Array.isArray(sortBy)) {
            return this.unmapSortField(sortBy[0][0], sortMap);
        } else {
            if (typeof sortBy === 'string' && sortBy.indexOf(':') > 0) {
                return this.unmapSortField(sortBy.split(':')[0], sortMap);
            } else {
                return this.unmapSortField(sortBy, sortMap);
            }
        }
    },
    parseSortDesc(sortBy) {
        // the db search sortDesc will return result like this: sort: [['firstName', 'desc']]
        // or returns sort: 'firstName:desc' or 'lastName:asc'
        if (Array.isArray(sortBy)) {
            return sortBy[0][1] === 'desc';
        } else {
            if (typeof sortBy === 'string' && sortBy.indexOf(':') > 0) {
                return sortBy.split(':')[1] === 'desc';
            } else {
                return false;
            }
        }
    },
    mapSortField(fieldName, sortMap) {
        // will map the UI sort field to the actual database sort field
        if (!isEmpty(sortMap) && fieldName && !isEmpty(sortMap[fieldName])) {
            return sortMap[fieldName];
        } else {
            return fieldName;
        }
    },
    unmapSortField(fieldName, sortMap) {
        // will map database sort field to the UI sort field
        if (!isEmpty(sortMap) && fieldName) {
            const field = Object.keys(sortMap).find(key => {
                return sortMap[key] === fieldName;
            });
            if (field) {
                return field;
            } else {
                return fieldName;
            }
        } else {
            return fieldName;
        }
    },
    extractChannelMapId(channelMapUrl) {
        // will extract the channelMapId from the channelMap full Url
        if (channelMapUrl) {
            const parts = channelMapUrl.split('/');
            const channelMapPartIndex = parts.findIndex(
                item => item === 'VenueChannelMap'
            );
            if (
                channelMapPartIndex >= 0 &&
                parts.length >= channelMapPartIndex + 1
            ) {
                return parts[channelMapPartIndex + 1];
            }
            return -1;
        } else {
            return -1;
        }
    }
};
